import React, { ReactElement, useMemo } from 'react';
import { alpha } from '@mui/material';
import BigNumber from 'bignumber.js';
import { makeStyles } from 'tss-react/mui';

import { useConnection } from 'modules/auth';
import {
  DEFAULT_SHORT_DECIMAL_PLACES,
  HUNDRED,
  ONE,
  ZERO,
} from 'modules/common';
import {
  globalTranslation,
  mergeTranslations,
  useTranslation,
} from 'modules/i18n';
import { StIotaTokenIcon } from 'modules/icons';
import { useGetBalancesQuery } from 'modules/stake';

import { useGetStatsQuery } from '../../actions/getStats';
import backgroundUrl from './assets/my-stake-bg.jpg';
import StarIcon from './assets/star.svg?react';
import { Line } from './Line';
import { translation } from './translation';

const mergedTranslation = mergeTranslations(globalTranslation, translation);

export function MyStake(): ReactElement {
  const { classes } = useStyles();
  const { t, keys } = useTranslation(mergedTranslation);

  const { data } = useGetStatsQuery();

  const { isConnected } = useConnection();

  const { data: balances } = useGetBalancesQuery(undefined, {
    skip: !isConnected,
  });

  const balanceStaked = balances?.staked ?? ZERO;
  const balanceNative = balances?.native ?? ZERO;
  const balanceTotal = balanceStaked.plus(balanceNative);

  const stakedPercent = balanceTotal.isZero()
    ? ZERO
    : balanceStaked
        .div(balanceStaked.plus(balanceNative))
        .multipliedBy(HUNDRED);

  const rate = data?.rate ?? ONE;
  const iotaUsdPrice = data?.iotaUsdPrice ?? ZERO;

  const stakedInNative = balanceStaked.div(rate);
  const stakedInUsd = stakedInNative.multipliedBy(iotaUsdPrice);

  const { data: stats } = useGetStatsQuery();

  const boostedApy = useMemo(
    () =>
      stats?.apyBoosted
        ? new BigNumber(stats?.apyBoosted)
            .decimalPlaces(
              DEFAULT_SHORT_DECIMAL_PLACES,
              BigNumber.ROUND_HALF_EVEN,
            )
            .toNumber()
        : undefined,
    [stats?.apyBoosted],
  );
  const stakingApy = useMemo(
    () =>
      stats?.apy
        ? new BigNumber(stats?.apy)
            .decimalPlaces(
              DEFAULT_SHORT_DECIMAL_PLACES,
              BigNumber.ROUND_HALF_EVEN,
            )
            .toNumber()
        : undefined,
    [stats?.apy],
  );

  return (
    <div className={classes.root}>
      <div className={classes.apyWrap}>
        {!!boostedApy && (
          <div className={classes.apy}>
            <StarIcon />

            {t(keys.boostedApy, { value: boostedApy })}
          </div>
        )}

        {!!stakingApy && (
          <div className={classes.apy}>
            {t(keys.stakingApy, { value: stakingApy })}
          </div>
        )}
      </div>

      <div className={classes.first}>
        <div className={classes.title}>{t(keys.myStake)}</div>

        <div className={classes.info}>
          <StIotaTokenIcon className={classes.image} />

          <div>
            <div className={classes.asset}>
              <div className={classes.value}>
                {balanceStaked.toFixed(DEFAULT_SHORT_DECIMAL_PLACES)}
              </div>

              <div>{t(keys.tokens.staked)}</div>
            </div>

            {!rate.isZero() && (
              <div className={classes.description}>
                {t(keys.description, {
                  token: t(keys.tokens.native),
                  amount: stakedInNative.toFixed(3),
                })}

                {!stakedInUsd.isZero() &&
                  t(keys.amountUsd, {
                    amountUsd: stakedInUsd.toFixed(
                      DEFAULT_SHORT_DECIMAL_PLACES,
                    ),
                  })}
              </div>
            )}
          </div>
        </div>
      </div>

      <Line stakedPercent={stakedPercent.dp(0).toNumber()} />
    </div>
  );
}

const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    position: 'relative',
    gap: theme.spacing(17),
    borderRadius: 12,
    backgroundColor: theme.palette.secondary.main,
    backgroundImage: `url(${backgroundUrl})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right top',
    backgroundSize: 'cover',
    padding: theme.spacing(6),
    color: theme.palette.background.default,
    boxShadow: `1px 1px 10px 2px ${theme.palette.secondary.main}`,

    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(9),
      padding: theme.spacing(4),
    },
  },
  first: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(11),

    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(11),
    },
  },
  title: {
    fontSize: 14,
    lineHeight: '17px',
    fontWeight: 600,

    [theme.breakpoints.down('sm')]: {
      fontSize: 12,
      lineHeight: '15px',
    },
  },
  info: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2.5),
  },
  image: {
    width: 60,

    [theme.breakpoints.down('sm')]: {
      width: 48,
    },
  },
  asset: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    fontWeight: 700,
  },
  value: {
    fontSize: 40,
    lineHeight: '48px',
    letterSpacing: '-1px',

    [theme.breakpoints.down('sm')]: {
      fontSize: 32,
      lineHeight: '39px',
    },
  },
  description: {
    opacity: 0.6,
    fontSize: 12,
    fontWeight: 500,
  },

  apyWrap: {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    right: theme.spacing(6),
    gap: theme.spacing(1.25),

    [theme.breakpoints.down('sm')]: {
      right: 0,
    },
  },

  apy: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    position: 'relative',
    fontSize: 14,
    borderRadius: 8,
    background: alpha(theme.palette.secondary.contrastText, 0.05),
    padding: theme.spacing(1.4, 2.25),
    color: theme.palette.background.default,

    '&:before': {
      display: 'block',
      content: '""',
      position: 'absolute',
      inset: '0',
      borderRadius: 8,
      padding: theme.spacing(0.25),
      background: `linear-gradient(96.09deg, ${alpha(theme.palette.secondary.contrastText, 0.1)} 0%, ${alpha(theme.palette.secondary.contrastText, 0)} 100%), linear-gradient(96.09deg, ${alpha(theme.palette.common.black, 0)} 0%, ${alpha(theme.palette.common.black, 0.1)} 100%)`,
      mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
      maskComposite: 'exclude',
    },

    [theme.breakpoints.down('sm')]: {
      right: theme.spacing(4),
    },
  },
}));
